/** One owner for scroll, navigation, announcements, sizing, and editor lifecycle. */
class MayaBathHeader extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.options = { signal: this.abort.signal };
    this.header = this.querySelector('.mb-header');
    this.bar = this.querySelector('[data-header-bar]');
    this.mobile = this.querySelector('[data-mobile-panel]');
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.scrim = this.querySelector('[data-menu-scrim]');
    this.dropdowns = [...this.querySelectorAll('[data-dropdown]')];
    this.announcement = this.querySelector('[data-announcement]');
    this.slides = [...this.querySelectorAll('[data-announcement-item]')];
    this.slide = 0;
    this.mobileQuery = matchMedia('(max-width: 1274px)');
    this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    this.lastY = Math.max(0, window.scrollY);
    this.anchorY = this.lastY;
    this.direction = 0;
    this.storageKey = `mb-header-announcement-${this.dataset.sectionId}`;
    try { if (sessionStorage.getItem(this.storageKey) === 'closed' && this.announcement) this.announcement.hidden = true; } catch { /* Navigation still works when storage is unavailable. */ }
    this.dropdowns.forEach(details => {
      details.querySelector('summary').setAttribute('aria-expanded', String(details.open));
      details.addEventListener('toggle', () => this.dropdownChanged(details), this.options);
    });
    this.addEventListener('click', event => this.handleClick(event), this.options);
    this.addEventListener('keydown', event => this.handleKey(event), this.options);
    document.addEventListener('click', event => { if (!this.contains(event.target)) this.closeMenus(false); }, this.options);
    document.addEventListener('focusin', event => { if (!this.contains(event.target) && !this.hasAttribute('data-mobile-open')) this.closeMenus(false); }, this.options);
    window.addEventListener('scroll', () => this.scheduleScroll(), { ...this.options, passive: true });
    window.addEventListener('pageshow', () => { this.closeMenus(false); this.lastY = Math.max(0, scrollY); this.anchorY = this.lastY; this.updateScroll(); this.measure(); }, this.options);
    this.mobileQuery.addEventListener('change', () => { this.closeMenus(false); this.measure(); }, this.options);
    this.motionQuery.addEventListener('change', () => this.rotate(), this.options);
    document.addEventListener('visibilitychange', () => this.rotate(), this.options);
    this.announcement?.addEventListener('mouseenter', () => { this.pauseAnnouncement = true; this.rotate(); }, this.options);
    this.announcement?.addEventListener('mouseleave', () => { this.pauseAnnouncement = false; this.rotate(); }, this.options);
    this.announcement?.addEventListener('focusin', () => { this.pauseAnnouncement = true; this.rotate(); }, this.options);
    this.announcement?.addEventListener('focusout', event => { if (!this.announcement.contains(event.relatedTarget)) { this.pauseAnnouncement = false; this.rotate(); } }, this.options);
    document.addEventListener('shopify:block:select', event => this.selectEditorBlock(event), this.options);
    document.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.closeMenus(false); this.rotate(); }, this.options);
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.header);
    this.resizeObserver.observe(this.bar);
    this.updateScroll();
    this.measure();
    this.rotate();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
    clearTimeout(this.rotationTimer);
    this.unlockPage();
  }

  measure() {
    this.headerHeight = Math.round(this.header.offsetHeight * 100) / 100;
    this.barHeight = this.bar.offsetHeight;
    this.syncOffsets();
  }

  syncOffsets() {
    const height = this.headerHeight || 0;
    if (!this.hasAttribute('data-mobile-open')) this.closedHeight = height;
    const contentHeight = this.closedHeight || height;
    const visible = this.hasAttribute('data-hidden') ? 0 : this.dataset.sticky === 'none' ? Math.max(0, height - scrollY) : height;
    const values = {
      '--header-height': `${height}px`,
      '--header-group-height': this.dataset.transparent === 'true' ? '0px' : `${contentHeight}px`,
      '--mb-header-content-space': `${contentHeight}px`,
      '--transparent-header-offset-boolean': '0',
      '--mb-header-visible-height': `${visible}px`,
      '--sticky-header-offset': visible ? `${this.barHeight || 0}px` : '0px',
    };
    this.offsets ||= {};
    for (const [name, value] of Object.entries(values)) {
      if (this.offsets[name] === value) continue;
      document.body.style.setProperty(name, value);
      this.offsets[name] = value;
    }
  }

  scheduleScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = 0; this.updateScroll(); });
  }

  updateScroll() {
    const max = Math.max(0, document.documentElement.scrollHeight - innerHeight);
    const y = Math.max(0, Math.min(scrollY, max));
    const threshold = 50;
    const delta = y - this.lastY;
    const direction = Math.sign(delta);
    if (direction && direction !== this.direction) { this.anchorY = this.lastY; this.direction = direction; }
    this.toggleAttribute('data-scrolled', y > threshold);
    const pinned = this.hasAttribute('data-menu-open') || this.editorSelected || this.querySelector(':focus-visible') || document.querySelector('dialog[open]:not([data-announcement])');
    if (pinned || y <= threshold || this.dataset.sticky !== 'scroll-up') this.removeAttribute('data-hidden');
    else if (Math.abs(y - this.anchorY) >= 10) this.toggleAttribute('data-hidden', direction > 0);
    this.header.dataset.stickyState = !this.hasAttribute('data-hidden') && y > threshold ? 'active' : 'inactive';
    this.lastY = y;
    this.syncOffsets();
  }

  dropdownChanged(details) {
    if (details.open) this.dropdowns.forEach(other => { if (other !== details) other.open = false; });
    this.dropdowns.forEach(item => item.querySelector('summary').setAttribute('aria-expanded', String(item.open)));
    this.syncMenuState();
  }

  syncMenuState() {
    const mobileOpen = this.hasAttribute('data-mobile-open');
    const desktopOpen = this.dropdowns.some(item => item.open);
    this.toggleAttribute('data-menu-open', mobileOpen || desktopOpen);
    this.scrim.hidden = !desktopOpen;
    if (mobileOpen || desktopOpen) this.removeAttribute('data-hidden');
    this.measure();
  }

  handleClick(event) {
    if (event.target.closest('[data-menu-toggle]')) { this.setMobile(!this.hasAttribute('data-mobile-open')); return; }
    if (event.target.closest('[data-menu-scrim]')) { this.closeMenus(true); return; }
    if (event.target.closest('[data-announcement-close]')) {
      this.announcement.hidden = true;
      try { sessionStorage.setItem(this.storageKey, 'closed'); } catch { /* Session dismissal is optional. */ }
      this.rotate(); this.measure(); return;
    }
    if (event.target.closest('.search-action, header-actions, a')) this.closeMenus(false);
  }

  setMobile(open, restoreFocus = true) {
    if (open && !this.mobileQuery.matches) return;
    this.dropdowns.forEach(details => { details.open = false; });
    this.toggleAttribute('data-mobile-open', open);
    this.mobile.hidden = !open;
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    if (open) this.lockPage();
    else { this.unlockPage(); if (restoreFocus) this.toggle.focus({ preventScroll: true }); }
    this.syncMenuState();
  }

  closeMenus(restoreFocus) {
    const active = this.dropdowns.find(details => details.open);
    this.dropdowns.forEach(details => { details.open = false; });
    if (this.hasAttribute('data-mobile-open')) this.setMobile(false, restoreFocus);
    else if (restoreFocus) active?.querySelector('summary').focus({ preventScroll: true });
    this.syncMenuState();
  }

  lockPage() {
    if (this.inertElements) return;
    document.body.classList.add('mb-header-menu-open');
    this.inertElements = [...document.querySelectorAll('#MainContent, .shopify-section-group-footer-group')].map(element => ({ element, inert: element.inert }));
    this.inertElements.forEach(({ element }) => { element.inert = true; });
  }

  unlockPage() {
    document.body.classList.remove('mb-header-menu-open');
    this.inertElements?.forEach(({ element, inert }) => { element.inert = inert; });
    this.inertElements = null;
  }

  handleKey(event) {
    if (event.key === 'Escape' && this.hasAttribute('data-menu-open')) { event.preventDefault(); event.stopPropagation(); this.closeMenus(true); return; }
    const summary = event.target.closest('[data-dropdown] > summary');
    if (summary && event.key === 'ArrowDown') {
      event.preventDefault(); summary.parentElement.open = true;
      summary.parentElement.querySelector('a')?.focus();
    }
    if (event.key !== 'Tab' || !this.hasAttribute('data-mobile-open')) return;
    const focusable = [...this.header.querySelectorAll('a, button, summary, input, [tabindex="0"]')].filter(el => el.getClientRects().length && !el.disabled && !el.closest('[hidden]'));
    const first = focusable[0], last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }

  showSlide(index) {
    this.slide = index;
    this.slides.forEach((slide, position) => { slide.hidden = position !== index; });
  }

  rotate() {
    clearTimeout(this.rotationTimer);
    if (this.slides.length < 2 || this.announcement?.hidden || this.motionQuery.matches || document.hidden || this.pauseAnnouncement || this.editorSelected) return;
    this.rotationTimer = setTimeout(() => { this.showSlide((this.slide + 1) % this.slides.length); this.rotate(); }, Number(this.dataset.rotation) * 1000);
  }

  selectEditorBlock(event) {
    if (!this.contains(event.target)) return;
    this.editorSelected = true;
    this.removeAttribute('data-hidden');
    const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
    if (index >= 0) { this.announcement.hidden = false; this.showSlide(index); this.rotate(); }
    const group = event.target.closest('.mb-header__item');
    if (group) {
      if (this.mobileQuery.matches) this.setMobile(true, false);
      else { const details = group.querySelector('details'); if (details) details.open = true; }
    }
  }
}
if (!customElements.get('mb-site-header')) customElements.define('mb-site-header', MayaBathHeader);
